import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { ChevronDown, ChevronRight, Pause, Play } from "lucide-react";
import type { Artist, FileData } from "../data";
import { StatusPill } from "./shared";
import FileRow from "./FileRow";
import ProjectReview from "./ProjectReview";
import Waveform from "./Waveform";
import {
  getFileUrl,
  groupFiles,
  isAudioFormat,
  pickDefaultAudioFile,
} from "../lib/media";

interface Props {
  isAdmin: boolean;
  selectedArtist: Artist;
  initialProjectId?: string | null;
  onOpenProjectConsumed?: () => void;
  onCloseProject?: () => void;
}

export default function Projects({
  isAdmin,
  selectedArtist,
  initialProjectId = null,
  onOpenProjectConsumed,
  onCloseProject,
}: Props) {
  const deleteArtistFile = useMutation(api.mutations.deleteArtistFile);

  const audioRef = useRef<HTMLAudioElement>(null);

  const [expandedProjects, setExpandedProjects] = useState<string[]>([]);
  const [openProjectId, setOpenProjectId] = useState<string | null>(null);

  // Inline expand player state (per expanded project)
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setOpenProjectId(null);
    setExpandedProjects([]);
    setActiveFileId(null);
    setIsPlaying(false);
    // Only reset navigation when switching artists, not on live file/project updates.
  }, [selectedArtist.id]);

  // Honor deep-link / overview open
  useEffect(() => {
    if (initialProjectId) {
      setOpenProjectId(initialProjectId);
      onOpenProjectConsumed?.();
    }
  }, [initialProjectId, onOpenProjectConsumed]);

  const ACTIVE_STATUSES = ["in_progress", "in-progress", "active", "setup", "pending", "revisions", "ready_for_review", "ready-for-review", "approved", "payment_required", "payment-required", "delivered", "needs_export", "pending_import"];

  const projectFiles = selectedArtist.projects
    .slice()
    .sort((a, b) => {
      const aActive = ACTIVE_STATUSES.includes(a.status ?? "active");
      const bActive = ACTIVE_STATUSES.includes(b.status ?? "active");
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;
      return 0;
    })
    .map((project) => ({
      project,
      files: selectedArtist.files.filter(
        (f) => f.projectId === project.id || (f as any).project === project.title,
      ),
    }));

  const activeFile = useMemo(() => {
    if (!activeFileId) return null;
    return selectedArtist.files.find((f) => f.id === activeFileId) ?? null;
  }, [activeFileId, selectedArtist.files]);

  const activeUrl = activeFile ? getFileUrl(activeFile) : undefined;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [activeUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !activeUrl) return;
    audio.load();
    setCurrentTime(0);
  }, [activeUrl]);

  function playFile(file: FileData) {
    const url = getFileUrl(file);
    if (!url || file.access !== "available" || !isAudioFormat(file.format, file.title)) {
      return;
    }
    const same = file.id === activeFileId;
    setActiveFileId(file.id);
    window.requestAnimationFrame(() => {
      const audio = audioRef.current;
      if (!audio) return;
      if (same && !audio.paused) {
        audio.pause();
        return;
      }
      void audio.play().catch((err) => {
        console.error("Audio play failed", err);
        setIsPlaying(false);
      });
    });
  }

  async function handleDelete(fileId: string) {
    await deleteArtistFile({ fileId: fileId as any });
  }

  function toggleProject(projectId: string) {
    setExpandedProjects((prev) => {
      const next = prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId];
      return next;
    });
  }

  if (openProjectId) {
    return (
      <ProjectReview
        mode={isAdmin ? "admin" : "artist"}
        projectId={openProjectId}
        onBack={() => {
          setOpenProjectId(null);
          onCloseProject?.();
        }}
      />
    );
  }

  return (
    <div className="space-stack">
      <audio ref={audioRef} preload="metadata" src={activeUrl} />

      {projectFiles.length > 0 ? (
        <div className="project-file-list projects-list-simple">
          {projectFiles.map(({ project, files }) => {
            const expanded = expandedProjects.includes(project.id);
            const listId = `project-files-${project.id}`;
            const grouped = groupFiles(files);
            const defaultAudio = pickDefaultAudioFile(files);
            const projectActiveFile =
              activeFile && files.some((f) => f.id === activeFile.id)
                ? activeFile
                : defaultAudio;
            const projectActiveUrl = projectActiveFile
              ? getFileUrl(projectActiveFile)
              : undefined;
            const projectIsPlaying =
              isPlaying &&
              !!activeFile &&
              files.some((f) => f.id === activeFile.id);

            return (
              <div key={project.id} className={`project-file-group ${expanded ? "expanded" : ""}`}>
                <div className={`project-file-heading ${expanded ? "open" : ""}`}>
                  <button
                    aria-controls={listId}
                    aria-expanded={expanded}
                    className="project-file-heading-main"
                    onClick={() => toggleProject(project.id)}
                    type="button"
                  >
                    <div>
                      <strong>{project.title}</strong>
                      <small>
                        {files.length} {files.length === 1 ? "file" : "files"}
                      </small>
                    </div>
                    <ChevronDown className={`h-4 w-4 chevron ${expanded ? "rotate-180" : ""}`} />
                  </button>
                  <span className="project-heading-actions">
                    <StatusPill status={project.status} />
                    <button
                      className="open-project-btn"
                      onClick={() => setOpenProjectId(project.id)}
                      type="button"
                    >
                      {["completed", "delivered"].includes(project.status)
                        ? "Open"
                        : "Full review"}
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </span>
                </div>

                {expanded ? (
                  <div className="project-expand-body" id={listId}>
                    {projectActiveUrl ? (
                      <div className="project-expand-player">
                        <div className="project-expand-player-head">
                          <div>
                            <p className="eyebrow">Preview</p>
                            <strong>{projectActiveFile?.title}</strong>
                          </div>
                          <button
                            type="button"
                            aria-label={`${projectIsPlaying ? "Pause" : "Play"} ${projectActiveFile?.title ?? "project audio"}`}
                            className={`soundcloud-play-button soundcloud-play-button-compact ${projectIsPlaying ? "playing" : ""}`}
                            onClick={() => {
                              if (projectActiveFile) playFile(projectActiveFile);
                            }}
                            title={projectIsPlaying ? "Pause track" : "Play track"}
                          >
                            {projectIsPlaying ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        <Waveform
                          audioUrl={
                            activeFile && files.some((f) => f.id === activeFile.id)
                              ? activeUrl
                              : projectActiveUrl
                          }
                          currentTime={
                            activeFile && files.some((f) => f.id === activeFile.id)
                              ? currentTime
                              : 0
                          }
                          duration={
                            activeFile && files.some((f) => f.id === activeFile.id)
                              ? duration
                              : 0
                          }
                          onSeek={(time) => {
                            if (audioRef.current) {
                              audioRef.current.currentTime = time;
                              setCurrentTime(time);
                            }
                          }}
                          height={96}
                          isPlaying={projectIsPlaying}
                        />
                      </div>
                    ) : null}

                    {grouped.length > 0 ? (
                      <div className="file-group-stack compact">
                        {grouped.map(({ group, files: groupFileList }) => (
                          <div className="file-group" key={group.id}>
                            <div className="file-group-heading static">
                              <div>
                                <strong>{group.label}</strong>
                                <small>
                                  {groupFileList.length}{" "}
                                  {groupFileList.length === 1 ? "file" : "files"}
                                </small>
                              </div>
                            </div>
                            <div className="file-group-list">
                              {groupFileList.map((file) => (
                                <div key={file.id} className="file-row compact-file-row">
                                  <div className="file-title-line">
                                    <strong>{file.title}</strong>
                                  </div>
                                  <FileRow
                                    canDownload={
                                      isAdmin ||
                                      project.status === "completed" ||
                                      (project.projectType === "3_track_bundle" &&
                                        project.priceCents === 0 &&
                                        project.status === "approved")
                                    }
                                    compact
                                    file={file}
                                    isAdmin={isAdmin}
                                    isActive={activeFileId === file.id}
                                    isPlaying={isPlaying && activeFileId === file.id}
                                    onDelete={handleDelete}
                                    onPlay={playFile}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-state">
                        {isAdmin
                          ? "No files or stems added for this project yet."
                          : "No files available for this project yet."}
                      </p>
                    )}

                    <div className="project-expand-footer">
                      <button
                        type="button"
                        className="primary-link"
                        onClick={() => setOpenProjectId(project.id)}
                      >
                        {["completed", "delivered"].includes(project.status)
                          ? "Open project"
                          : "Open full project review"}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="empty-state projects-list-empty">
          {isAdmin ? "Add a project before files can be attached." : "No projects yet."}
        </p>
      )}
    </div>
  );
}
